/**
 * SearchResults Component
 *
 * Container for search results with keyboard navigation.
 * Features:
 * - Arrow up/down to navigate results
 * - Enter to select
 * - ARIA listbox/option roles for accessibility
 * - Empty state with helpful message
 * - Scroll selected item into view
 *
 * Story 2-4: SearchResults Component
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { SearchResult } from '@functieloonhuis/shared';
import { SearchResultItem } from './SearchResultItem';

interface SearchResultsProps {
  results: SearchResult[];
  onSelect: (slug: string) => void;
}

export function SearchResults({ results, onSelect }: SearchResultsProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isKeyboardNavigating = useRef(false);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (!isKeyboardNavigating.current) return;

    const selectedElement = document.getElementById(`search-result-option-${selectedIndex}`);
    if (selectedElement && typeof selectedElement.scrollIntoView === 'function') {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    isKeyboardNavigating.current = false;
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          isKeyboardNavigating.current = true;
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          isKeyboardNavigating.current = true;
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex].slug);
          }
          break;
      }
    },
    [results, selectedIndex, onSelect]
  );

  // Attach keyboard listener to document for global navigation
  // Note: Uses document level to capture arrow keys regardless of focus
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Empty state
  if (results.length === 0) {
    return (
      <div
        className="p-4 text-center text-gray-500"
        data-testid="search-no-results"
        role="status"
      >
        <p>Geen resultaten gevonden</p>
        <p className="text-sm mt-1">Probeer een andere zoekterm</p>
      </div>
    );
  }

  return (
    <ul
      className="divide-y divide-gray-100"
      role="listbox"
      aria-label={`Zoekresultaten: ${results.length} gevonden`}
      aria-activedescendant={`search-result-option-${selectedIndex}`}
      data-testid="search-results-list"
    >
      {results.map((result, index) => (
        <SearchResultItem
          key={result.id}
          result={result}
          isSelected={index === selectedIndex}
          onSelect={() => onSelect(result.slug)}
          index={index}
        />
      ))}
    </ul>
  );
}
